import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  FormControlLabel,
  Switch,
  Slider,
  IconButton,
} from '@mui/material';
import {
  Close as CloseIcon,
  School as ConceptIcon,
  Description as DocumentIcon,
  Build as SkillIcon,
  Work as UseCaseIcon,
  Settings as ProcessIcon,
  Folder as ResourceIcon,
  Warning as RiskIcon,
  Security as ControlIcon,
  Analytics as MetricIcon,
  Person as RoleIcon,
} from '@mui/icons-material';
import { NodeType, CreateNodeForm } from '../../types/knowledge';

interface AddNodeModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (nodeData: CreateNodeForm) => Promise<void>;
}

const nodeTypeColors: Record<NodeType, string> = {
  'Concept': '#FFC107',
  'Document': '#009688',
  'Skill': '#00BCD4',
  'UseCase': '#4CAF50',
  'Process': '#9C27B0',
  'Resource': '#795548',
  'Risk': '#F44336',
  'Control': '#3F51B5',
  'Metric': '#00BCD4',
  'Role': '#2196F3',
};

const getNodeIcon = (type: NodeType): React.ReactElement => {
  switch (type) {
    case 'Concept': return React.createElement(ConceptIcon);
    case 'Document': return React.createElement(DocumentIcon);
    case 'Skill': return React.createElement(SkillIcon);
    case 'UseCase': return React.createElement(UseCaseIcon);
    case 'Process': return React.createElement(ProcessIcon);
    case 'Resource': return React.createElement(ResourceIcon);
    case 'Risk': return React.createElement(RiskIcon);
    case 'Control': return React.createElement(ControlIcon);
    case 'Metric': return React.createElement(MetricIcon);
    case 'Role': return React.createElement(RoleIcon);
    default: return React.createElement(ConceptIcon);
  }
};

export function AddNodeModal({ open, onClose, onSubmit }: AddNodeModalProps) {
  const [formData, setFormData] = useState<CreateNodeForm>({
    name: '',
    type: 'Concept',
    description: '',
    color: nodeTypeColors['Concept'],
    size: 20,
    is_user_scope: true,
    is_team_scope: false,
  });

  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    if (!formData.name.trim()) return;

    setLoading(true);
    try {
      await onSubmit(formData);
      handleClose();
    } catch (error) {
      console.error('Error creating node:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      name: '',
      type: 'Concept',
      description: '',
      color: nodeTypeColors['Concept'],
      size: 20,
      is_user_scope: true,
      is_team_scope: false,
    });
    onClose();
  };

  const handleTypeChange = (type: NodeType) => {
    setFormData(prev => ({
      ...prev,
      type,
      color: nodeTypeColors[type],
    }));
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6">Add Knowledge Node</Typography>
          <IconButton onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 1 }}>
          {/* Node Name */}
          <TextField
            label="Node Name"
            value={formData.name}
            onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
            fullWidth
            required
            placeholder="Enter a name for this knowledge node"
          />

          {/* Node Type */}
          <FormControl fullWidth>
            <InputLabel>Node Type</InputLabel>
            <Select
              value={formData.type}
              onChange={(e) => handleTypeChange(e.target.value as NodeType)}
              label="Node Type"
            >
              {Object.keys(nodeTypeColors).map((type) => (
                <MenuItem key={type} value={type}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {getNodeIcon(type as NodeType)}
                    <Typography>{type}</Typography>
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Description */}
          <TextField
            label="Description"
            value={formData.description}
            onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
            fullWidth
            multiline
            rows={3}
            placeholder="Describe this knowledge node"
          />

          {/* Node Size */}
          <Box>
            <Typography gutterBottom>Node Size: {formData.size}</Typography>
            <Slider
              value={formData.size}
              onChange={(_, value) => setFormData(prev => ({ ...prev, size: value as number }))}
              min={10}
              max={50}
              step={5}
              marks={[
                { value: 10, label: 'Small' },
                { value: 30, label: 'Medium' },
                { value: 50, label: 'Large' },
              ]}
            />
          </Box>

          {/* Scope Settings */}
          <Box>
            <Typography gutterBottom>Scope</Typography>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.is_user_scope}
                    onChange={(e) => setFormData(prev => ({ ...prev, is_user_scope: e.target.checked }))}
                  />
                }
                label="Personal"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.is_team_scope}
                    onChange={(e) => setFormData(prev => ({ ...prev, is_team_scope: e.target.checked }))}
                  />
                }
                label="Team"
              />
            </Box>
          </Box>

          {/* Preview */}
          <Box>
            <Typography gutterBottom>Preview</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2, border: 1, borderColor: 'divider', borderRadius: 1 }}>
              <Box
                sx={{
                  width: Math.sqrt(formData.size) * 2,
                  height: Math.sqrt(formData.size) * 2,
                  borderRadius: '50%',
                  backgroundColor: formData.color,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  minWidth: 40,
                  minHeight: 40,
                }}
              >
                {getNodeIcon(formData.type)}
              </Box>
              <Box>
                <Typography variant="subtitle1" fontWeight="bold">
                  {formData.name || 'Node Name'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {formData.type}
                </Typography>
                {formData.description && (
                  <Typography variant="caption" color="text.secondary">
                    {formData.description}
                  </Typography>
                )}
              </Box>
            </Box>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={!formData.name.trim() || loading}
        >
          {loading ? 'Creating...' : 'Create Node'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
